import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SIZE_STYLES = {
  sm: {
    nav: 'gap-0.5 text-xs',
    backIcon: 'h-3.5 w-3.5',
    sepIcon: 'h-3 w-3',
    crumbPad: 'px-0.5',
  },
  md: {
    nav: 'gap-1 text-sm',
    backIcon: 'h-4 w-4',
    sepIcon: 'h-3.5 w-3.5',
    crumbPad: 'px-1',
  },
};

/**
 * Presentational breadcrumb trail — no router dependency.
 * Pair with app-specific code that builds `items` from the current route.
 *
 * @example
 * import { BreadcrumbNav } from '@aimsos/ui';
 *
 * <BreadcrumbNav
 *   items={[
 *     { label: 'Governance', path: '/governance' },
 *     { label: 'Drives', path: '/governance/source-drives' },
 *   ]}
 *   onNavigate={(path) => navigate(path)}
 * />
 */
const BreadcrumbNav = ({
  items,
  onNavigate,
  onBack,
  size = 'md',
  className = '',
}) => {
  if (!items?.length) return null;

  const s = SIZE_STYLES[size] ?? SIZE_STYLES.md;

  const handleBack =
    onBack ??
    (() => {
      if (items.length >= 2) {
        onNavigate(items[items.length - 2].path);
      }
    });

  return (
    <nav
      aria-label="Breadcrumb"
      className={['flex items-center', s.nav, className].filter(Boolean).join(' ')}
    >
      {items.length > 1 && (
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center justify-center p-0.5 rounded text-content-faint hover:text-content transition-colors cursor-pointer"
          aria-label="Go back"
        >
          <ChevronLeft className={s.backIcon} />
        </button>
      )}

      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <React.Fragment key={item.path ?? `breadcrumb-${index}`}>
            {index > 0 && (
              <ChevronRight
                className={`${s.sepIcon} text-content-faint shrink-0`}
              />
            )}

            {isLast ? (
              <span
                className={`text-content font-medium ${s.crumbPad} truncate`}
              >
                {item.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(item.path)}
                className={`text-content-faint hover:text-content transition-colors cursor-pointer ${s.crumbPad} truncate`}
              >
                {item.label}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default BreadcrumbNav;
